import { Box, HStack, MenuItem, Text, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { MaterialIcon } from './MaterialIcon';

interface SubMenuItemProps {
  /** Visible text. Truncate this freely — it is not the accessible name. */
  label: string;
  /**
   * Full, untruncated accessible name, when `label` has been shortened to fit.
   * Without it a long collection is announced as "Add to Marine Microplastics
   * Biodegradation…" — the ellipsis hides exactly the part that disambiguates.
   */
  srLabel?: string;
  /**
   * Muted second line under `label` — for state the trigger describes rather
   * than repeats, e.g. the current workspace under "Switch workspace". Folded
   * into the accessible name so it is announced with the label instead of being
   * silently visual-only.
   */
  description?: string;
  icon?: React.ReactElement<any>;
  children: React.ReactNode;
  isDisabled?: boolean;
  /**
   * Keeps a disabled trigger reachable by the arrow keys, so a keyboard or
   * screen-reader user meets it and its reason (`aria-describedby`).
   */
  isFocusable?: boolean;
  'aria-describedby'?: string;
}

/**
 * SubMenuItem — a nested group inside a Chakra menu, expanded IN PLACE.
 *
 * It used to be a hover flyout: a portalled `MenuList` positioned by hand with
 * `position="fixed"` + top/left. That never worked, and could not have.
 * `MenuList` is Popper-driven and only positions inside a `Menu` context — used
 * standalone it computes to `position: static` and writes its OWN inline
 * `transform`/`opacity`/`visibility`, which beat any style prop. The flyout
 * therefore ignored every coordinate it was given and rendered at the
 * top-left corner of the viewport. Measured in a real browser:
 *
 *   actual_position: "static", actual_top: "auto", actual_left: "auto"
 *
 * Two rounds of "fix the position" (direction, collision-flip, viewport clamp,
 * rAF re-measure on scroll) were all computing coordinates nothing consumed.
 *
 * Expanding in place removes the entire class of problem instead of the
 * instance:
 *  - no geometry, so nothing to mis-place, flip, clamp or re-measure
 *  - the children are real `MenuItem`s in the real `MenuList`, so Chakra's own
 *    keyboard navigation covers them. The flyout was mouse-only — `onMouseEnter`
 *    with no keyboard route in at all.
 *  - nothing is portalled, so it cannot survive its parent and strand itself
 *    on screen (Escape used to close the menu and leave the flyout behind)
 *  - it works identically on touch, where hover doesn't exist
 *
 * The cost is a taller menu while open, which is why the trigger stays collapsed
 * by default and the group is indented against a rule so the nesting reads.
 */
export const SubMenuItem: React.FC<SubMenuItemProps> = ({
  label,
  srLabel,
  description,
  icon,
  children,
  isDisabled,
  isFocusable,
  'aria-describedby': describedBy,
}) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <>
      <MenuItem
        icon={icon}
        // The trigger opens the group; it is not itself a destination.
        closeOnSelect={false}
        onClick={onToggle}
        isDisabled={isDisabled}
        isFocusable={isFocusable}
        aria-describedby={describedBy}
        // `aria-expanded` ALONE. Not `aria-haspopup`, which promises a submenu
        // POPUP — the entire point of this rewrite is that there isn't one, the
        // items are real MenuItems in the same MenuList. Announcing "submenu"
        // sends the user reaching for Right-Arrow, which does nothing here.
        aria-expanded={isOpen}
        // The visible label is truncated to keep the menu narrow, so it can't
        // also be the accessible name — assistive tech would be read the
        // ellipsis instead of the end of the collection's name.
        aria-label={srLabel ?? (description ? `${label}, ${description}` : undefined)}
      >
        <HStack w="full" justify="space-between" spacing={2}>
          <Box minW={0} textAlign="left">
            <Text isTruncated>{label}</Text>
            {description && (
              <Text fontSize="xs" color="text.muted" isTruncated>
                {description}
              </Text>
            )}
          </Box>
          <MaterialIcon name={isOpen ? 'expand_less' : 'expand_more'} boxSize={4} />
        </HStack>
      </MenuItem>

      {isOpen && (
        // Indented against a rule so the group reads as belonging to the row
        // above it. Arrow-key order survives the wrapper because Chakra's
        // descendants context sorts by `compareDocumentPosition` — DOM order,
        // which nesting preserves — not by being a direct child.
        //
        // `role="group"` is load-bearing, not decoration: `role="menu"` may
        // only own menuitem / group / separator, so a bare div here is an axe
        // violation and can hide the nested items from AT that walks
        // required-owned-elements.
        <Box
          role="group"
          // `srLabel` first for the same reason as the trigger above: falling
          // back to `label` would name the group with the truncated string,
          // ellipsis and all — exactly what the trigger goes out of its way to
          // avoid.
          aria-label={srLabel ?? label}
          pl={2}
          ml={3}
          borderLeft="1px solid"
          borderColor="border.muted"
        >
          {children}
        </Box>
      )}
    </>
  );
};
